// Custom presets in localStorage (a per-browser convenience; failures are ignored).

import { DEFAULT_SETTINGS, mergeSettings, type Preset, type Settings } from '../core/settings';

const KEY = 'sid2midi.presets.v1';

export function loadCustomPresets(): Preset[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as { id: string; name: string; settings: unknown }[];
    return list
      .filter((p) => p && typeof p.name === 'string')
      .map((p) => ({
        id: p.id,
        name: p.name,
        description: 'Custom preset',
        settings: mergeSettings(DEFAULT_SETTINGS, p.settings),
      }));
  } catch {
    return [];
  }
}

export function saveCustomPresets(presets: Preset[]) {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify(presets.map((p) => ({ id: p.id, name: p.name, settings: p.settings }))),
    );
  } catch {
    /* storage unavailable (private mode, quota) */
  }
}

/** Settings that are specific to a file and should not travel with presets. */
export function withoutFileSpecific(s: Settings): Settings {
  return { ...s, subtune: 0 };
}

export function presetToJson(name: string, s: Settings): string {
  return JSON.stringify({ app: 'sid2midi-web', version: 1, name, settings: withoutFileSpecific(s) }, null, 2);
}

export function presetFromJson(text: string): { name: string; settings: Settings } {
  const data = JSON.parse(text) as { name?: unknown; settings?: unknown };
  if (!data || typeof data !== 'object' || !data.settings)
    throw new Error('This JSON file does not contain SID2MIDI settings.');
  return {
    name: typeof data.name === 'string' ? data.name : 'Imported preset',
    settings: mergeSettings(DEFAULT_SETTINGS, data.settings),
  };
}
